import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { measureElement as measureRow, observeWindowOffset, useWindowVirtualizer } from '@tanstack/react-virtual'
import { Search, X } from 'lucide-react'
import { EventRow } from '../components/EventRow'
import { EventSheet } from '../components/EventSheet'
import { KindHeading } from '../components/KindHeading'
import { ScreenHead } from '../components/ScreenHead'
import { Hidden } from './Hidden'
import { Places, PlacesChips, PlacesSort, placesOf, placeMatches, NONE_FILTER, type Place, type PlacesFilter } from './Places'
import type { ShowingLite } from '../components/CardBody'
import { Banner, Button, Chip, Empty, Field, Row, Segmented, Sheet } from '../ui'
import {
  useAffinity, useEnsembles, useEntryState, useFeed, usePeople, useSeries, useShowings, useStays, useVenues,
  type VenueRow,
} from '../lib/queries'
import { seriesName, seriesNights } from '../lib/night'
import { useSeen } from '../lib/seen'
import { secondHomeOf, useProfile } from '../lib/profile'
import { currentRegion, homeName, homeRegionOf, useRegionsNow } from '../lib/regions'
import { useEntryWriteError } from '../lib/mutations'
import { indexShowings, laneFor } from '../lib/derive'
import { iso } from '../lib/calendar'
import { FORM_LABEL, classicalForm, type ClassicalForm } from '../lib/classicalForm'
import {
  EN_LABEL, alreadyHappened, cityOf, heroUrl, idsOf, inReach, isDayTrip, isHere,
  languageAllowed, mergeFilmRuns, offFeed, swatch, talkLanguageAllowed, SORTS, type SortKey,
} from '../lib/entries'
import { freshOf } from '../lib/newIn'
import { awayFromEvent } from '../lib/away'
import { go, replace, TAB_RESET, useHeldWhileHidden, useRoute } from '../lib/router'
import {
  clusterByKind, hiddenRows, KIND_DOT, KIND_LABEL, KINDS, inWindow, kindOf, matches as rowMatches,
  modeOf, rankedByKind, PHONE_WHEN, WINDOWS, whenOf,
  type BrowseMode, type Kind, type Showing as BrowseShowing, type When,
} from '../lib/browse'
import { isSoldOut, useSoldOut } from '../lib/tickets'
import type { FeedRow } from '../lib/types'
import type { AffinityMap } from '../lib/affinity'
import { Loading, Failed } from './Stub'

/** One identity for "nothing yet". */
const NONE_YET: never[] = []

/** One identity for "no affinity data yet" (Phase E4, K5) — `useAffinity`'s
 *  own empty state, so a still-loading or failed query lifts nothing rather
 *  than holding up the rank (rule 7). */
const NONE_AFFINITY: AffinityMap = new Map()

/** How many rows each kind section shows before it hands the rest to its own
 *  "N more" line — WP 6.3, item 2, kept as WP 8.3's own cap now that a
 *  section is the whole screen's shape rather than one day's. Only applies
 *  when no kind chip narrows the screen to one section already. */
const KIND_CAP = 5

/* The When windows and their words are `browse.ts`'s `WINDOWS` (a4-14):
   the desk's five, worded the desk's way, on both shells. WP 8.3 moved the
   control into the Filters sheet; `?when=` is the same URL vocabulary a link
   from Today ("All N" → `when=tonight`) already writes. */

/** F6's own three, in the Filters sheet — `entries.ts`'s full `SORTS` also
 *  has Latest and Most mentioned, which belong to the type pages this
 *  package deletes and to nothing Browse's brief asks for. */
const BROWSE_SORTS: SortKey[] = ['best', 'soonest', 'title']

interface Filters {
  /** `trips` is blind spots A3: day trips only (`reachOf === 'day_trip'`). */
  where: 'reach' | 'here' | 'trips' | 'all'
  lang: string[]
  district: string[]
  form: string[]
  org: string[]
}

const NONE_FILTERS: Filters = { where: 'reach', lang: [], district: [], form: [], org: [] }

type Facet = 'lang' | 'district' | 'form' | 'org'

/** One item, in the virtualised list — a kind heading, or a row under it.
 *  Flattened so `@tanstack/react-virtual` can measure and position the
 *  whole thing as one list rather than a list of lists. WP 8.3 drops the
 *  day heading this used to carry a third variant for: Browse ranks the
 *  events themselves now, and stops grouping by date entirely. */
type Item =
  | { kind: 'kindhead'; k: Exclude<Kind, 'all'>; count: number }
  | { kind: 'row'; e: FeedRow; under?: Exclude<Kind, 'all'> }
  /** "N more concerts · see all concerts", after the section's last shown
   *  row — the rows it is "more" than are above it (UI audit a1-25; it used
   *  to sit between the heading and the first row). */
  | { kind: 'kindmore'; k: Exclude<Kind, 'all'>; hidden: number }

const toggle = (list: string[], v: string): string[] =>
  list.includes(v) ? list.filter(x => x !== v) : [...list, v]

/**
 * Browse: what kind, and — since WP 8.3 — the events themselves ranked by
 * `browseRank`, not grouped by date; When moved off the header into the
 * Filters sheet. And, since WP 6.3, Places's other half: an Events/Places
 * segmented control at the top of the one screen rather than a fifth tab.
 *
 * This was Feed and its type pages (`FeedType.tsx`, deleted). F2's two
 * controls — when and what kind — replace the old lane-by-lane preview with
 * one list windowed by `@tanstack/react-virtual` (X6): nothing renders more
 * than a screenful of `EventRow`s to the DOM, however wide the "Any time"
 * window gets, which the ~30-row lanes on the old Feed never had to solve
 * because a lane never showed more than its quota.
 *
 * `when`, `kind`, `mode` and `q` live in the URL (WP 3.2's own rewrite of
 * `parseHash`, extended by WP 6.3 for `mode`) so Today's search box has
 * somewhere to hand a query (`#/browse?q=…`), so `#/places…` has somewhere
 * to land (`router.ts`'s alias writes `mode=places` itself), and so a
 * filtered or a Places link is a link that still means the same thing after
 * a reload. Sort and the facet chips do not: the brief asks for them in the
 * Filters sheet only, and a sheet's own state reopening exactly where it was
 * left is not a promise the URL needs to keep — the same is true of the
 * Bezirk/Near-me state Places mode reads, which stays local for the same
 * reason.
 */
export function Browse() {
  const now = new Date()
  const today = iso(now)
  const route = useRoute()
  // Gated on `route.tab`: every screen shares one `useRoute()`, and the second
  // hash segment belongs to whichever tab the hash actually names —
  // `#/lists/<id>` while Browse sits hidden behind X7's `hidden` div must not
  // read that list id as a kind or place deep-link. `App.tsx` guards the same
  // way for Lists, just from outside — Browse can do it itself because it
  // reads the whole route directly instead of being handed just the id.
  const routeId = route.tab === 'browse' ? route.id : null
  const query = route.tab === 'browse' ? route.query : {}
  const mode: BrowseMode = modeOf(query)

  const [open, setOpen] = useState<FeedRow | null>(null)
  const [sheet, setSheet] = useState(false)
  const [f, setF] = useState<Filters>(NONE_FILTERS)
  const [sort, setSort] = useState<SortKey>('best')
  const [qInput, setQInput] = useState(query.q ?? '')
  const [searchOpen, setSearchOpen] = useState(false)
  const [mapOpen, setMapOpen] = useState(false)
  const [placesFilter, setPlacesFilter] = useState<PlacesFilter>(NONE_FILTER)

  // Near me's own permission ask — fired once, the moment the chip is first
  // turned on, the same trigger 3.4c used. The profile's home point stands in
  // until (or instead of) an answer — `Places` reads it; this is never a
  // second home point of Browse's own, which is the exact bug CLAUDE.md's
  // list warns about (`distanceKm` grew an `origin` argument rather than a
  // screen rolling its own haversine).
  useEffect(() => {
    if (!placesFilter.nearMe || placesFilter.geo || !('geolocation' in navigator)) return
    navigator.geolocation.getCurrentPosition(
      pos => setPlacesFilter(p => ({ ...p, geo: { lat: pos.coords.latitude, lng: pos.coords.longitude } })),
      () => {}, // denied, unavailable, or timed out — the profile's home already stands in
      { maximumAge: 5 * 60 * 1000 },
    )
  }, [placesFilter.nearMe, placesFilter.geo])

  const feedQ = useFeed()
  const stateQ = useEntryState()
  const staysQ = useStays()
  const showQ = useShowings()
  const venuesQ = useVenues()
  // WP 8.4: seen rows leave every event list, the same as dismissed ones.
  const seenQ = useSeen()
  // Phase E4 (K5): Doug's own history with the people and ensembles on a
  // row, folded into `best`'s ranking below.
  const affinityQ = useAffinity()
  // U1.2: the language gates are the person's own (`profiles.english_only`,
  // `talks_language`), not Doug's written as constants.
  const profile = useProfile()
  const { english_only: englishOnly, talks_language: talksLanguage } = profile
  const secondLabel = secondHomeOf(profile)?.label.trim() || null
  // C2.1: the region being looked at (reach) and the home region (what
  // "away" is away from). The bundled copy until the tables answer.
  const regions = useRegionsNow()
  const region = currentRegion(regions)
  const homeId = homeRegionOf(profile)
  const homeLabel = homeName(regions, profile)
  const where = useMemo(() => ({ home: { id: homeId, name: homeLabel }, towns: regions.towns }), [homeId, homeLabel, regions.towns])
  // Above every early return: see the `pending` branch below.
  const writeError = useEntryWriteError()

  // a4-13: held while Browse is hidden, as Today's are — a save on Today
  // re-derives Browse when Browse is next shown, not behind its `hidden` div.
  const shown = route.tab === 'browse'
  const feedRows = useHeldWhileHidden(feedQ.data, shown)
  const stateRows = useHeldWhileHidden(stateQ.data, shown)
  const stayRows = staysQ.data ?? NONE_YET
  const rawShowings = showQ.data?.rows
  const venuesById = venuesQ.data?.byId

  // WP 8.3: default is Any time, not This week — Browse is about the events
  // themselves, not the date (Doug: "I care less about when in the week").
  // a4-14 kept that default (`PHONE_WHEN`) and added the desk's Next 30
  // days; a link that writes a window explicitly (`SECTION_WHEN`'s
  // `when=tonight`) is unaffected.
  const urlWhen: When = whenOf(query.when) ?? PHONE_WHEN
  // a1-09: the Filters sheet's When is staged here while the sheet is open,
  // like every other control in it — the list and "Show N events" follow it
  // live, and the URL is written once, on close (`closeSheet`). Writing
  // `?when=` on tap assigned `location.hash` under an open sheet, which fires
  // `popstate`, which the sheet reads as Back: When was the one control that
  // slammed the sheet shut.
  const [sheetWhen, setSheetWhen] = useState<When | null>(null)
  const when: When = sheetWhen ?? urlWhen
  const kind: Kind = query.kind && (query.kind === 'all' || (KINDS as string[]).includes(query.kind))
    ? (query.kind as Kind) : 'all'
  // Nightlife N7: `?series=<id>`, a party's (or quiz's) page — every future
  // night with an edge to it, host nights first, then its home venue as a
  // place link. `?person=`'s shape (an id in the URL, the name from the
  // table), but its own list rather than a filter on the ranked one, as
  // `?new=1` is: "every night of this party" is not Events' when/kind/saved
  // stack applied to a smaller set — a saved night, or one in week eleven,
  // is still one of its nights. Entry: the eyebrow on a club night's sheet.
  const series = mode === 'events' ? query.series || null : null
  const newOnly = mode === 'events' && !series && query.new === '1'
  const seriesOnly = !!series
  // Phase E5 (K6): one person or one ensemble, by id, set by a chip tap on
  // the sheet. In the URL beside `when`, `kind` and `q` for the same reason
  // they are — a filtered Browse is a link that still means the same thing
  // after a reload. Never both at once: `browseQuery` writes one key, and
  // reading only one here means a hand-edited URL carrying both narrows by
  // the person rather than silently ANDing two axes that were never meant to
  // compose with each other.
  const person = query.person || null
  const ensemble = person ? null : query.ensemble || null
  // The name behind the id. The URL carries the id and nothing else — a name
  // in a link is a name that goes stale — and the row's own `people` is
  // `[{id, role}]` (K11), so the one place the header can read a name is the
  // table the id points at. One small request, only while a filter is on,
  // cached for the hour.
  const personQ = usePeople(person ? [person] : NONE_YET)
  const ensembleQ = useEnsembles(ensemble ? [ensemble] : NONE_YET)
  // Always called (one shared, persisted query — the club rows ask for it
  // too), read only while `?series=` is on.
  const seriesQ = useSeries()
  const seriesRow = series ? seriesQ.data?.get(series) ?? null : null
  const seriesTitle = series
    ? seriesName(seriesQ.data, series) ?? (seriesQ.isSuccess || seriesQ.isError ? series : null)
    : null
  const entityName = seriesTitle ?? (person
    ? personQ.data?.find(p => p.id === person)?.name ?? (personQ.isSuccess ? person : null)
    : ensemble
      ? ensembleQ.data?.find(s => s.id === ensemble)?.name ?? (ensembleQ.isSuccess ? ensemble : null)
      : null)

  const setQuery = useCallback((patch: Record<string, string | undefined>, opts?: { replace?: boolean }) => {
    const next: Record<string, string> = { ...query }
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === '') delete next[k]; else next[k] = v
    }
    if (opts?.replace) replace('browse', null, next); else go('browse', null, next)
  }, [query])

  const switchMode = useCallback((next: BrowseMode) => {
    // Kind/when/new are Events-only vocabulary and a place id means nothing
    // to Events, so a mode switch drops all three rather than leaving a
    // kind chip silently selected somewhere Places never draws it. The
    // search query survives the switch — the FAB above both modes is one
    // search box, not two that happen to share a button.
    setQuery({
      mode: next === 'places' ? 'places' : undefined,
      kind: undefined, when: undefined, new: undefined,
      // A person is an Events axis too: Places groups buildings, and a
      // conductor filter left switched on there would narrow nothing while
      // still showing as a chip. A series page likewise.
      person: undefined, ensemble: undefined, series: undefined,
    })
  }, [setQuery])

  const openPlace = useCallback((id: string | null) => {
    go('browse', id, { ...(query.q ? { q: query.q } : {}), mode: 'places' })
  }, [query.q])

  // Rule 4: `#/browse/<type>` (an old `#/feed/<type>` lands here through
  // `ALIASES`) is a deep link that sets the chip, normalised to the query
  // form on arrival — there is one canonical URL for "Browse, filtered to
  // concerts" and it is `?kind=concert`, not a second path a reload has to
  // keep recognising forever. Events mode only: in Places mode the same
  // second segment is a venue id, which `Places` itself reads as `openId`.
  //
  // Replaced, never pushed (a1-03): a push left `#/browse/film` under
  // `?kind=film` in history, and Back from one landed on the other, which
  // normalised forward again — three Back presses stayed on Browse.
  useEffect(() => {
    if (mode === 'events' && routeId && routeId !== 'hidden' && query.kind !== routeId) {
      setQuery({ kind: routeId }, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per id/mode.
  }, [routeId, mode])

  // a1-02: Browse follows `?q=` whenever it arrives — Today's search box
  // submitting, a link, Back to a search. The box used to be seeded from the
  // URL once, at mount, and X7 mounts every tab at startup, so Today's Enter
  // landed on an unfiltered Browse with the box shut, at whatever scroll
  // position Browse was left at. `routeQ` is null while another tab is
  // showing (its `query` is not ours to read). An absent `q` leaves the box
  // alone: a tab-bar switch lands on a bare `#/browse`, and the search
  // surviving that round trip is behaviour this does not change.
  //
  // Scrolled on the next frame, not here: `App.tsx` restores Browse's last
  // scroll position on arrival, the box's own state change renders the
  // filtered list a commit later, and the virtualizer re-measures after
  // both — a `scrollTo` here was overtaken by all three (rendered: it landed
  // at 255px). An arriving query is a new list, read from the top.
  const routeQ = route.tab === 'browse' ? (query.q ?? '') : null
  const qInputRef = useRef(qInput)
  qInputRef.current = qInput
  useEffect(() => {
    if (!routeQ) return
    setSearchOpen(true)
    // Our own debounced write coming back — or (a1-32) Browse shown again on
    // the search it was left on, whose list and scroll are still where they
    // were. Only a different query is a new list, read from the top.
    if (routeQ === qInputRef.current) return
    setQInput(routeQ)
    const f = requestAnimationFrame(() => window.scrollTo(0, 0))
    return () => cancelAnimationFrame(f)
  }, [routeQ])

  // The search box debounces its own writes to the URL: a `go` per keystroke
  // would fill the back button with one entry per letter typed. Never while
  // another tab is showing — a write fired 400ms after a tab switch would
  // drag Doug back to Browse.
  const browseShowing = useRef(route.tab === 'browse')
  browseShowing.current = route.tab === 'browse'
  useEffect(() => {
    const t = setTimeout(() => {
      if (!browseShowing.current) return
      if (qInput !== (query.q ?? '')) setQuery({ q: qInput })
    }, 400)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only `qInput` should retrigger the debounce.
  }, [qInput])

  // a1-32: a tap on Browse while Browse is showing goes to its index — the
  // bare `#/browse` (`App.tsx`'s `tabTarget`) — and drops what the hash does
  // not hold: the Filters sheet's facets and sort, the search box, Places'
  // Bezirk and Near me. Doug's own phrasing of the finding: "a second Browse
  // tap resets".
  useEffect(() => {
    const onReset = (ev: Event) => {
      if ((ev as CustomEvent).detail !== 'browse') return
      setF(NONE_FILTERS); setSort('best'); setSheetWhen(null)
      setQInput(''); setSearchOpen(false)
      setPlacesFilter(p => ({ ...NONE_FILTER, geo: p.geo }))
    }
    window.addEventListener(TAB_RESET, onReset)
    return () => window.removeEventListener(TAB_RESET, onReset)
  }, [])

  // A chip tap writes `?person=` and nothing else, but the search box keeps
  // its own state — so the box still held whatever was last typed, and 400ms
  // later the debounce above put it straight back in the URL. "Everything by
  // this conductor" quietly became "everything by this conductor matching
  // words in a box that is not even on screen". Arriving at an entity filter
  // empties the box.
  //
  // Keyed on the filter arriving, not on `q` leaving the URL: a tab switch
  // also lands on a `#/browse` with no `q` (`App.tsx`'s `switchTab` calls
  // `go(tab)`), and the search surviving that round trip is behaviour this
  // phase has no business changing. Typing *after* the filter is on is
  // untouched too — rule 4 says `?person` composes with `q`.
  useEffect(() => {
    if (!person && !ensemble && !series) return
    setQInput(v => (v === '' ? v : ''))
    setSearchOpen(false)
  }, [person, ensemble, series])

  // `?day=` used to arrive from Today's day view ("All N" on a single date,
  // WP 3.1). 8.8 proved it did nothing once it got here — `#/browse` and
  // `#/browse?day=<date>` rendered byte-identical screens — because
  // `browse.ts` has no "which day is this row on" a filter could key off;
  // this effect only ever forced `when` to Any time, which every row already
  // reads that way once When moved off the header (8.3). WP 8.9 removed the
  // link itself (`Today.tsx`'s day view) rather than build that filter, so
  // nothing in the app writes `?day=` any more — this handling went with it.
  // Wiring a real day filter into `browse.ts` is the bigger fix and is a call
  // for whoever owns this file next; don't re-add the query-param handling
  // without it.

  const showings = useMemo<ShowingLite[]>(() => {
    if (!rawShowings || !feedRows) return NONE_YET
    const venueCoords = new Map<string, { lat: number | null; lng: number | null }>()
    for (const r of feedRows) {
      if (r.venue_id && !venueCoords.has(r.venue_id)) venueCoords.set(r.venue_id, { lat: r.lat, lng: r.lng })
    }
    return rawShowings.map(s => ({ ...s, ...(s.venue_id ? venueCoords.get(s.venue_id) : undefined) }))
  }, [rawShowings, feedRows])

  const showingsByEntry = useMemo(() => indexShowings(showings), [showings])
  const browseShowings: BrowseShowing[] = showings

  // Keyed on the dismissed ids themselves, not on `stateRows`: a save is an
  // `entry_state` change that dismisses nothing, and a new Set here re-ran
  // `base` — the whole feed's filter and film merge — and everything below it
  // on every heart (a4-13). Only `saved`, below, needs to move on a save.
  const dismissedKey = useMemo(
    () => (stateRows ?? NONE_YET).filter(r => r.state === 'dismissed').map(r => r.entry_id).sort().join('\n'),
    [stateRows])
  const dismissed = useMemo(
    () => new Set(dismissedKey ? dismissedKey.split('\n') : NONE_YET),
    [dismissedKey])
  const seen = seenQ.data
  // The Hidden page's own list, counted (a1-06) — not `dismissed.size`, which
  // also counts dismissals whose entry has left the feed and that the page
  // therefore cannot show.
  const hiddenCount = useMemo(
    () => hiddenRows(stateRows ?? NONE_YET, feedRows ?? NONE_YET).length,
    [stateRows, feedRows])

  // WP 8.3: saved rows leave the ranked events list, the same as dismissed
  // and seen ones — a row Doug already saved is not a recommendation any
  // more, so it should not cost one of the five slots a kind gets. Applied
  // in `searched` below, not `base`: Places mode and "New in town" both
  // read `base` and are untouched, and `matches` still finds a saved row
  // once Doug is asking a question rather than being shown a shortlist
  // (the plan's own call, README's Needs Doug 1 — a veto Doug can still
  // make). This is the one named place the exclusion lives.
  const saved = useMemo(
    () => new Set((stateRows ?? NONE_YET).filter(r => r.state === 'saved').map(r => r.entry_id)),
    [stateRows])

  // The same base every list of events in the app runs through — dismissed,
  // seen, language, talk language (WP 6.3, item 3), hidden lanes,
  // already-happened, away — before Browse's own controls narrow it further,
  // and before Places mode groups it by building instead of by day. Kept
  // identical to `Today.tsx`'s so the two screens cannot disagree about what
  // "the feed" means.
  const { base, trips } = useMemo(() => {
    const rows = feedRows ?? NONE_YET
    const trips = stayRows.filter(s => s.kind === 'travel' && s.ends_on >= today)
    const away = stayRows.filter(s => (s.kind === 'travel' || s.kind === 'home2') && s.ends_on >= today)

    // Since cards WP3 the view sends a film's set as one row, so this merges
    // only what the twins pass has not joined (`mergeFilmRuns`' own note).
    const visible = mergeFilmRuns(
      rows
        .filter(e => !dismissed.has(e.id) && !seen?.has(e.id)
          && languageAllowed(e, englishOnly) && talkLanguageAllowed(e, talksLanguage))
        .filter(e => !alreadyHappened(e, today))
        .filter(e => classicalForm(e) !== 'educational')
        .filter(e => !laneFor(e).hidden && laneFor(e).id !== 'news'),
      showings,
      today,
    )

    const base = visible.filter(e => !awayFromEvent(
      { city: cityOf(e), run_start: e.run_start, run_end: e.run_end, published_on: e.published_on },
      away,
      where,
      secondLabel,
    ))

    return { base, trips }
  }, [feedRows, stayRows, dismissed, seen, showings, today, englishOnly, talksLanguage, secondLabel, where])

  // Places mode's own grouping — computed once, here, so the header's Bezirk
  // chips and `Places`' own list read the same buildings and can never
  // silently disagree about what one is (CLAUDE.md's "one value, two jobs",
  // read the other way round: two computations of the same thing).
  const places = useMemo<Place[]>(() => {
    if (!venuesById) return NONE_YET
    return placesOf(base, showings, venuesById)
  }, [base, showings, venuesById])

  const placesShown = useMemo(
    () => places.filter(p => placeMatches(p, qInput)),
    [places, qInput])

  /** Every facet filter but `except` — how many rows a chip in that other
   *   category would leave, given everything already chosen. */
  const applyFacets = useCallback((rows: FeedRow[], except: Facet | null): FeedRow[] => rows.filter(e => {
    // E5, rule 4: `?person` composes with when, kind, q and the facet chips
    // with AND. By id against the row's own `people`/`ensembles` —
    // `feed_lite` carries `[{id, role}]` on every row for exactly this (K11),
    // so the filter costs no request and works on a row whose sheet has never
    // been opened. Not a facet: it has no chip in the Filters sheet and no
    // count to leave out of, so `except` never names it.
    //
    // `?? []` is not defensive typing: a row cached in IndexedDB before E1
    // added the column has no `people` at all, and `feed_since` only re-sends
    // a row whose `updated_at` moved. Without the guard the first such row
    // threw, there is no error boundary, and `#/browse?person=…` rendered a
    // blank app that reloaded blank (Doug, 29 Sept).
    if (person && !(e.people ?? []).some(p => p.id === person)) return false
    if (ensemble && !(e.ensembles ?? []).some(s => s.id === ensemble)) return false
    if (f.where === 'reach' && !inReach(e, trips, region, regions.towns)) return false
    if (f.where === 'here' && !isHere(e, region, regions.towns)) return false
    if (f.where === 'trips' && !isDayTrip(e, region, regions.towns)) return false
    if (except !== 'lang' && f.lang.length && !f.lang.includes(e.english_access)) return false
    if (except !== 'form' && f.form.length && !f.form.includes(classicalForm(e) ?? '')) return false
    if (except !== 'district' && f.district.length && !(e.district && f.district.includes(e.district))) return false
    if (except !== 'org' && f.org.length && !(e.organisation && f.org.includes(e.organisation))) return false
    return true
  }), [f, trips, person, ensemble, region, regions.towns])

  const whenScoped = useMemo(
    () => base.filter(e => inWindow(e, when, now, browseShowings)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `now` is fresh every render; the Berlin day is what these care about.
    [base, when, today, browseShowings])

  // a1-27, Doug's choice: a card whose every future night is sold out leaves
  // the lists — the ranked one, New in town and a series' nights — and comes
  // back for a search, as a saved row does. Its sheet still says sold out.
  const soldOutMarks = useSoldOut()
  const soldOut = useCallback((e: FeedRow) => {
    if (soldOutMarks.size === 0) return false
    const own = idsOf(e).flatMap(id => showingsByEntry.get(id) ?? NONE_YET)
    return isSoldOut(e, own, soldOutMarks, new Date())
  }, [soldOutMarks, showingsByEntry])

  const searched = useMemo(() => {
    const isSearching = qInput.trim() !== ''
    return whenScoped.filter(e => rowMatches(e, qInput) && (isSearching || (!saved.has(e.id) && !soldOut(e))))
  }, [whenScoped, qInput, saved, soldOut])

  const filtered = useMemo(() => applyFacets(searched, null), [searched, applyFacets])

  const kindCounts = useMemo(() => {
    const counts = new Map<Kind, number>()
    counts.set('all', filtered.length)
    for (const e of filtered) counts.set(kindOf(e), (counts.get(kindOf(e)) ?? 0) + 1)
    return counts
  }, [filtered])

  const kindScoped = useMemo(
    () => (kind === 'all' ? filtered : filtered.filter(e => kindOf(e) === kind)),
    [filtered, kind])

  const sorted = useMemo(() => [...kindScoped].sort(SORTS[sort].cmp), [kindScoped, sort])

  // `?new=1` — item 2's own deep link. A flat, uncapped, kind-clustered list
  // under one heading rather than a day window: "new" is not a day, and the
  // set is small enough that a cap would only ever hide the thing the link
  // exists to show. `base` rather than `searched`/`filtered`: new-in-town is
  // its own view, not Events' when/kind/facet stack applied to a different
  // input — but the shared search box still narrows it, the one control
  // that stays meaningful across both.
  //
  // a1-16: `freshOf` is the list Today's section draws, and the rows it runs
  // over are narrowed the way Today's are — in reach and on the feed — so
  // "All N" there lands on N here.
  const newRows = useMemo(
    () => freshOf(base.filter(e => inReach(e, trips, region, regions.towns) && !offFeed(e)), today)
      .map(x => x.e)
      .filter(e => rowMatches(e, qInput) && (qInput.trim() !== '' || !soldOut(e))),
    [base, trips, today, qInput, soldOut, region, regions.towns])

  // `?series=`: `base`, like New in town — already past, dismissed, seen and
  // away are out; saved nights and every week of the horizon are in. Host
  // nights first, each half soonest first (`seriesNights`). `?? []` inside
  // it: a row cached before `feed_page` sent `series` has no key (E5).
  const seriesRows = useMemo(
    () => (series ? seriesNights(base.filter(e => !soldOut(e)), series, e => e.next_showing ?? e.run_start) : NONE_YET),
    [base, series, soldOut])
  const seriesHome = seriesRow?.home_venue_id ? venuesById?.get(seriesRow.home_venue_id) ?? null : null
  // A place page exists only for a building with something on (below, where
  // the home is drawn).
  const seriesHomeLinked = !!seriesHome && places.some(p => p.id === (seriesHome.parent_id ?? seriesHome.id))

  // WP 8.3: no day headings, no matter the sort. `best` ranks each kind
  // section by `browseRank` — critic scores and urgency already folded into
  // the one number rows sort on, so the section itself is the replacement
  // mechanism the brief asks for (a saved/dismissed/seen row is already out
  // of `kindScoped` by the time this runs, and the sixth becomes the fifth
  // with nothing else to do here). `soonest` keeps `clusterByKind`'s own
  // arrival-order cap (rows already sorted soonest-first by `sorted`), and
  // `title` stays the flat, uncapped A–Z list it already was — a kind
  // heading over an alphabetised list would be as much fiction as a date
  // heading was. A kind chip selected drops the sections/cap entirely:
  // "the whole kind ranked by browseRank" per the brief, one flat list.
  const affinity = affinityQ.data ?? NONE_AFFINITY
  const items = useMemo<Item[]>(() => {
    if (sort === 'title') return sorted.map(e => ({ kind: 'row', e }))
    const out: Item[] = []
    if (kind === 'all') {
      const sections = sort === 'best'
        ? rankedByKind(kindScoped, now, { cap: KIND_CAP, affinity })
        : clusterByKind(sorted, { cap: KIND_CAP })
      for (const c of sections) {
        out.push({ kind: 'kindhead', k: c.kind, count: c.rows.length + c.hidden })
        for (const e of c.rows) out.push({ kind: 'row', e, under: c.kind })
        if (c.hidden > 0) out.push({ kind: 'kindmore', k: c.kind, hidden: c.hidden })
      }
    } else {
      const rows = sort === 'best' ? rankedByKind(kindScoped, now, { affinity }).flatMap(c => c.rows) : sorted
      for (const e of rows) out.push({ kind: 'row', e })
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `now` is fresh every render; only the Berlin day `browseRank` reads off it needs to be stable within one.
  }, [sorted, sort, kind, kindScoped, affinity])

  /** The morep line's own tap (WP 6.3, item 2): set the kind chip. No day to
   *  scroll back to any more — WP 8.3 drops the heading `bday-<date>` used
   *  to name. */
  const jumpToKind = useCallback((k: Exclude<Kind, 'all'>) => {
    setQuery({ kind: k })
  }, [setQuery])

  // WP 8.3: a non-default When now lives in the Filters sheet rather than on
  // a header chip Doug could see at a glance, so the sheet's own badge counts
  // it — the one place left that says a filter is narrowing the screen.
  const activeFilterCount =
    (f.where !== 'reach' ? 1 : 0) + f.lang.length + f.district.length + f.form.length + f.org.length
    + (when !== PHONE_WHEN ? 1 : 0)
    // E5: counted because Reset clears it. A badge that says "2" and a Reset
    // that drops three things is the badge lying about what it stands for.
    + (person || ensemble || series ? 1 : 0)

  // ---------------------------------------------------------------------
  // X6: the virtualised list. `useWindowVirtualizer` because the document is
  // what scrolls in this app (X7) — an internal scroll container here would
  // be the one screen with its own scrollbar. WP 8.3 drops the sticky day
  // heading this used to carry a custom `rangeExtractor` for: every item is
  // now a plain absolutely-positioned one, the default range extractor's own
  // shape.
  // ---------------------------------------------------------------------

  // Read early — `listReady` below needs it, ahead of where the loading
  // and error returns actually happen. `venuesQ` joins the other four here
  // (rather than only inside `Places`, which also checks its own copy for
  // the detail page's sake) so Places mode's chip row never draws off an
  // empty `places` array on the first paint, before venues have arrived.
  const pending = feedQ.isPending || stateQ.isPending || staysQ.isPending || showQ.isPending || venuesQ.isPending

  const listRef = useRef<HTMLDivElement>(null)
  const [scrollMargin, setScrollMargin] = useState(0)
  // Two ways this used to read 0 forever, both from the one-time, mount-only
  // effect this replaces (`useLayoutEffect(() => {...}, [])`):
  //
  //  1. X7 keeps every tab mounted, so Browse's first mount can land inside
  //     `<div hidden>` while Today is the one showing — a hidden subtree's
  //     `offsetTop` is 0.
  //  2. Even on a direct `#/browse` load, that first mount can still be
  //     *pending*: query data has not arrived yet, so this screen's own
  //     early return draws the skeleton instead of the list, and `listRef`
  //     is never attached to a real node.
  //
  // Either way, a `[]` dependency array meant the wrong (or absent)
  // measurement was permanent — every row and heading below the first
  // screenful then rendered a header's-height too far down, real content
  // took over from a sticky heading before the real scroll position said it
  // should, and both together are the jumping heading Doug reported
  // (`docs/ux-plan/wp-5.4-browse.md` Part 3, candidate 1). Re-measuring
  // whenever this tab is the visible one *and* has something to measure,
  // and on resize — the kind chips can wrap to a second line — keeps it
  // honest instead.
  const listReady = route.tab === 'browse' && mode === 'events' && !newOnly && !seriesOnly && !pending
  useLayoutEffect(() => {
    if (!listReady) return
    const measure = () => setScrollMargin(listRef.current?.offsetTop ?? 0)
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [listReady])

  // 120 is an `EventRow` after WP 5.1: its 96px leading slot decides the row's
  // height (the slot is taller than the tallest the text can be, which is rule
  // 1 and why every kind is one box), plus `.u-row__main`'s 12px of padding top
  // and bottom. Carried across at the merge of 5.1 and 5.4, which is why it was
  // 76 on 5.4's own branch: 5.1 was not in it yet.
  //
  // Worth getting close rather than merely non-zero. An item measured for the
  // first time on scroll reflows everything below it, which reads as the page
  // jumping — one of Part 3's three candidates, and the complaint this package
  // exists to fix. Leaving 76 against a 120px row would have re-created it from
  // the other direction.
  // a4-13: the window is shared by every tab, so the virtualiser's own scroll
  // listener re-rendered this list — mounting and unmounting `EventRow`s
  // behind its `hidden` div — while Doug scrolled Today, Lists or Year.
  // Hidden, it ignores the window's scroll; its offset stays where Browse last
  // left it, which is exactly the position `App.tsx` restores (X7) when Browse
  // is shown again, so the rows it holds are already the right ones. The
  // other half of keeping a hidden list still — rows that measured 0px under
  // `display: none` — is a1-02's `measureElement` below.
  const shownRef = useRef(shown)
  shownRef.current = shown
  const virtualizer = useWindowVirtualizer({
    count: items.length,
    estimateSize: i => (items[i]?.kind === 'kindhead' ? 32 : items[i]?.kind === 'kindmore' ? 45 : 120),
    overscan: 6,
    scrollMargin,
    // a1-02: arriving on Browse, it ran itself down the list — to the
    // bottom, on a fresh load (traced: 55 `scrollTo` calls from
    // `resizeItem`, 0 -> 3250px, nobody touching the screen). X7 keeps
    // Browse mounted in a hidden pane, where every row measured 0px, and the
    // virtualizer kept those sizes: on the way back each row re-measured
    // from 0, and its rule for holding the view still read every 0px row at
    // the top as "above the fold" once the first correction had moved the
    // window — one row after another, to the end. A row in a hidden pane
    // (no `offsetParent`) keeps the size it last had instead, so the list
    // comes back the height it left at.
    measureElement: (el, entry, inst) => {
      if ((el as HTMLElement).offsetParent === null) {
        const i = Number(el.getAttribute('data-index'))
        const known = inst.measurementsCache[i]?.size
        if (known) return known
      }
      return measureRow(el, entry, inst)
    },
    observeElementOffset: (instance, cb) =>
      observeWindowOffset(instance, (offset, isScrolling) => { if (shownRef.current) cb(offset, isScrolling) }),
  })



  const showingsFor = (e: FeedRow): ShowingLite[] => {
    const ids = idsOf(e)
    if (ids.length === 1) return showingsByEntry.get(ids[0]) ?? NONE_YET
    const set = new Set(ids)
    return showings.filter(s => set.has(s.entry_id))
  }

  const error = feedQ.error ?? stateQ.error ?? staysQ.error ?? showQ.error ?? venuesQ.error

  // A place's own page and the dismissed list draw their own heads once
  // loaded (below), so the Events/Places row and its chips are not the shape
  // these two would swap to. Defined here, above the early returns, for the
  // pending and failed branches to read; the same two flags decide the loaded
  // head further down.
  const placeDetailOpen = mode === 'places' && !!routeId
  const hiddenOpen = mode === 'events' && routeId === 'hidden'

  // WP 10.7 (10.6 finding, per 10.1's own report): these early returns draw
  // before the row below's `ScreenHead` mounts, so a slow or failed load
  // left the account button unreachable. They used to draw a bare title, which
  // put the account button back but swapped the whole top of the screen to the
  // Events/Places control, Filters and the chip row on arrival; the audit
  // measured it as the pending header being "a different shape". Now the
  // real head is drawn with its data-dependent parts inert: the segmented
  // control still works (a mode is a URL, it needs no data), Filters/Map is
  // disabled, and the chips are the same pills with no counts. Only the list
  // area below changes when the data lands.
  //
  // Not a hook and not conditional on one: `pending`/`error` only decide what
  // this function returns, every hook above has already run — the WP 6.9 trap.
  if (pending || error) {
    // Row skeletons, the shape of the list that replaces them — Today's own
    // five (UI audit a1-37). The default three bars are a paragraph, and the
    // list arriving over them was a second change of shape.
    const below = pending ? <Loading variant="row" count={5} /> : <Failed error={error!.message} />
    if (placeDetailOpen || hiddenOpen) {
      return <><ScreenHead className="browsebar" lead={<h1 className="screen-title">Browse</h1>} />{below}</>
    }
    return (
      <>
        <ScreenHead
          className="browsebar"
          lead={
            <Segmented
              value={mode}
              onChange={v => switchMode(v as BrowseMode)}
              options={[{ value: 'events', label: 'Events' }, { value: 'places', label: 'Places' }]}
              aria-label="Browse mode"
            />
          }
          trailing={<Button variant="secondary" size="sm" disabled>{mode === 'events' ? 'Filters' : 'Map'}</Button>}
        />
        <div className="browse__kinds" role="group" aria-label={mode === 'events' ? 'Kind' : 'District'}>
          {mode === 'events' ? (
            <>
              <Chip on={kind === 'all'} disabled>All</Chip>
              {KINDS.map(k => (
                <Chip key={k} dot={KIND_DOT[k]} disabled>{KIND_LABEL[k]}</Chip>
              ))}
            </>
          ) : (
            <Chip on disabled>All</Chip>
          )}
        </div>
        {below}
      </>
    )
  }

  // A save takes the row out of the list only where `searched` says so: the
  // ranked Events list with no search typed. "New in town" reads `base` and a
  // search still finds saved rows, so there the heart just fills.
  const savedLeaves = mode === 'events' && !newOnly && !seriesOnly && qInput.trim() === ''

  /** a1-09: the one way the Filters sheet closes — Show N, the scrim, Escape
   *  or Back — and the one place its staged When reaches the URL. A replace,
   *  so it rewrites the sheet's own history entry into the filtered URL
   *  (`router.ts`'s `replace`), leaving Back to undo the filter, as it did
   *  when the chip lived in the header. */
  const closeSheet = () => {
    if (sheetWhen !== null && sheetWhen !== urlWhen) {
      setQuery({ when: sheetWhen === PHONE_WHEN ? undefined : sheetWhen, new: undefined }, { replace: true })
    }
    setSheetWhen(null)
    setSheet(false)
  }

  const row = (e: FeedRow, under?: Exclude<Kind, 'all'>) => (
    <EventRow key={e.id} e={e} showings={showingsFor(e)} leavesOnSave={savedLeaves} under={under}
              onOpen={ev => setOpen(ev)} />
  )

  // The FAB and its search bar hide wherever the header they float above
  // does not apply — the dismissed list, and a place's own page, the same
  // two "not really Browse's own chrome" cases the header below bails out
  // of first.
  const showChrome = !hiddenOpen && !placeDetailOpen

  if (hiddenOpen) {
    return (
      <>
        <Hidden onBack={() => go('browse', null, query)} />
        <EventSheet e={open} onClose={() => setOpen(null)} />
      </>
    )
  }

  return (
    <>
      {/* WP 6.3, item 1: Events/Places on the left, the mode's own action on
          the right, then one chip row — under 110px at 390px wide,
          measured. The search box that used to live here (`.browse__search`)
          is the floating button below instead. */}
      {!placeDetailOpen && (
        <>
          {/* WP 10.1: the same row, as a `ScreenHead` — the account button
              joins it last. `browsebar` stays as the render scripts' hook. */}
          <ScreenHead
            className="browsebar"
            lead={
              <Segmented
                value={mode}
                onChange={v => switchMode(v as BrowseMode)}
                options={[{ value: 'events', label: 'Events' }, { value: 'places', label: 'Places' }]}
                aria-label="Browse mode"
              />
            }
            trailing={mode === 'events'
              ? (
                <Button variant="secondary" size="sm" onClick={() => setSheet(true)}>
                  Filters{activeFilterCount > 0 ? ` · ${activeFilterCount}` : ''}
                </Button>
              )
              : (
                <Button variant="secondary" size="sm" onClick={() => setMapOpen(true)}>
                  Map
                </Button>
              )}
          />

          <div className="browse__kinds" role="group" aria-label={mode === 'events' ? 'Kind' : 'District'}>
            {mode === 'events' ? (
              <>
                {/* E5, rule 4: the entity the list is narrowed to, with an ×
                    that clears it — the same "tap to drop this filter" the
                    search bar's close button is, and the only thing on screen
                    that says why a conductor's four concerts are all there is.
                    First in the row because it is the strongest narrowing in
                    force; the kind chips still apply on top of it (AND). */}
                {entityName && (
                  <button
                    type="button"
                    className="u-chip is-on browse__person"
                    aria-label={`Clear the ${entityName} filter`}
                    onClick={() => setQuery({ person: undefined, ensemble: undefined, series: undefined })}
                  >
                    <span className="u-chip__face">
                      <span className="browse__person-name">{entityName}</span>
                      <X size={14} className="browse__person-x" aria-hidden="true" />
                    </span>
                  </button>
                )}
                <Chip on={kind === 'all' && !newOnly && !seriesOnly} count={kindCounts.get('all') ?? 0}
                      onClick={() => setQuery({ kind: undefined, new: undefined, series: undefined })}>All</Chip>
                {KINDS.map(k => (
                  <Chip key={k} on={kind === k && !newOnly && !seriesOnly} dot={KIND_DOT[k]} count={kindCounts.get(k) ?? 0}
                        onClick={() => setQuery({ kind: k, new: undefined, series: undefined })}>
                    {KIND_LABEL[k]}
                  </Chip>
                ))}
              </>
            ) : (
              <PlacesChips places={placesShown} filter={placesFilter} onChange={setPlacesFilter} />
            )}
          </div>
        </>
      )}

      {writeError && <Banner tone="bad">{writeError}</Banner>}

      {mode === 'places' && !placeDetailOpen && <PlacesSort filter={placesFilter} onChange={setPlacesFilter} />}

      {mode === 'places' ? (
        <Places
          places={placesShown} showings={showings} openId={routeId} onOpen={openPlace}
          filter={{ ...placesFilter, q: qInput }} mapOpen={mapOpen} onCloseMap={() => setMapOpen(false)}
        />
      ) : seriesOnly ? (
        <>
          {/* "Nights", not the party's name: the chip above already says
              it, and the same name twice in 130px is a page with two
              headings for one thing. */}
          <div className="rowhead">
            <h2>Nights</h2>
            <span className="g">{seriesRows.length}</span>
          </div>
          {seriesRows.length === 0
            ? <Empty title="No nights coming up." />
            : seriesRows.map(e => row(e))}
          {/* The series' home, as a place link — Places' own place row
              (`.ph2`, the building's picture or its swatch, name, district)
              and the place page's own route (`openPlace`), so it looks and
              lands exactly as a Places tap does. Absent for a party that
              roams (`home_venue_id` null) and while venues are in flight.
              A link only where the place page exists: `Places` draws a page
              only for a building with something on, and sends any other id
              to its index (its own note, "a route can point at a place that
              no longer has anything upcoming") — so a club with nothing
              listed yet (Else, until its recipe runs) is named, not linked,
              rather than a tap that lands on 211 other places. */}
          {seriesHome && (
            <>
              <div className="rowhead"><h2>Home</h2></div>
              <SeriesHome
                venue={seriesHome}
                onOpen={seriesHomeLinked ? () => openPlace(seriesHome.parent_id ?? seriesHome.id) : null}
              />
            </>
          )}
        </>
      ) : newOnly ? (
        <>
          <div className="rowhead">
            <h2>New in town</h2>
            <span className="g">{newRows.length}</span>
          </div>
          {newRows.length === 0
            ? <Empty title={qInput ? `Nothing matching “${qInput}”.` : 'Nothing new right now.'} />
            : clusterByKind(newRows).map(c => (
                <div key={c.kind}>
                  <KindHeading kind={c.kind} count={c.rows.length} />
                  {c.rows.map(e => row(e, c.kind))}
                </div>
              ))}
        </>
      ) : items.length === 0 ? (
        <Empty title={qInput ? `Nothing matching “${qInput}”.` : 'Nothing here right now.'} />
      ) : (
        <div ref={listRef}>
          <div style={{ position: 'relative', height: virtualizer.getTotalSize(), width: '100%' }}>
            {virtualizer.getVirtualItems().map(vi => {
              const item = items[vi.index]
              const style: CSSProperties = {
                position: 'absolute', top: 0, left: 0, width: '100%',
                transform: `translateY(${vi.start - scrollMargin}px)`,
              }
              return (
                <div key={vi.key} data-index={vi.index} ref={virtualizer.measureElement} style={style}>
                  {item.kind === 'kindhead'
                    ? <KindHeading kind={item.k} count={item.count} />
                    : item.kind === 'kindmore'
                      ? (
                        <button className="morep" onClick={() => jumpToKind(item.k)}>
                          <b>{item.hidden} more</b> {KIND_LABEL[item.k].toLowerCase()}
                          {' · see all '}{KIND_LABEL[item.k].toLowerCase()}
                        </button>
                      )
                      : row(item.e, item.under)}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {mode === 'events' && !newOnly && !seriesOnly && hiddenCount > 0 && (
        <Row
          // `u-row--flush`: nothing leads this row, so no empty slot either
          // (a1-37) — the title starts where the rows above start.
          className="browse__hidden u-row--flush"
          leading={null}
          title={`Hidden · ${hiddenCount}`}
          meta={null}
          onPress={() => go('browse', 'hidden', query)}
        />
      )}

      {showChrome && <div className="fab-clearance" aria-hidden="true" />}

      {showChrome && (
        searchOpen ? (
          <div className="searchbar">
            <Search size={20} aria-hidden="true" />
            <Field.Input
              autoFocus type="search" value={qInput} onChange={ev => setQInput(ev.currentTarget.value)}
              aria-label="Search events, people and places" placeholder="Search events, people, places"
              className="searchbar__input"
            />
            <button
              className="searchbar__close" aria-label="Close search"
              onClick={() => { setSearchOpen(false); setQInput(''); setQuery({ q: undefined }) }}
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <button className="fab" aria-label="Search" onClick={() => setSearchOpen(true)}>
            <Search size={22} />
          </button>
        )
      )}

      {sheet && (
        <FilterSheet
          f={f} setF={setF} sort={sort} setSort={setSort}
          when={when} setWhen={setSheetWhen}
          rows={searched} applyFacets={applyFacets} count={filtered.length}
          onClose={closeSheet}
          onReset={() => {
            setF(NONE_FILTERS)
            setSheetWhen(PHONE_WHEN)
            // Replaced, not pushed, for the same reason When is staged: a
            // push under the open sheet reads to it as Back and closes it.
            if (person || ensemble || series) {
              setQuery({ person: undefined, ensemble: undefined, series: undefined }, { replace: true })
            }
          }}
        />
      )}

      <EventSheet e={open} onClose={() => setOpen(null)} />
    </>
  )
}

/** A series' home venue in Places' own row (`.ph2`): the building's picture
 *  or its swatch, the name, the district. A button to the place page when
 *  `onOpen` is given, the same row as plain text when it is not. */
function SeriesHome({ venue, onOpen }: { venue: VenueRow; onOpen: (() => void) | null }) {
  const inner = (
    <>
      <div className="ph2__img">
        {venue.hero_key
          ? <img src={heroUrl({ venue_hero_key: venue.hero_key } as FeedRow, 112) ?? ''}
                 width={44} height={44} alt="" loading="lazy" decoding="async" />
          : <span className="phfill" style={{ background: swatch(venue.id) }} />}
      </div>
      <div className="ph2__text">
        <p className="ph2__name">{venue.name}</p>
        {venue.district && <p className="ph2__meta">{venue.district}</p>}
      </div>
    </>
  )
  return onOpen ? <button className="ph2" onClick={onOpen}>{inner}</button> : <div className="ph2">{inner}</div>
}

function FilterSheet({ f, setF, sort, setSort, when, setWhen, rows, applyFacets, count, onClose, onReset }: {
  f: Filters
  setF: (f: Filters) => void
  sort: SortKey
  setSort: (s: SortKey) => void
  /** WP 8.3: the header's own chip moves in here, `?when=` unchanged. */
  when: When
  setWhen: (w: When) => void
  /** Everything the current when/kind/search allow, before this sheet's own
   *  facets — what a chip's live count is measured against. */
  rows: FeedRow[]
  applyFacets: (rows: FeedRow[], except: Facet | null) => FeedRow[]
  count: number
  onClose: () => void
  onReset: () => void
}) {
  // C2.1: the Where segments name the region being looked at ("Berlin").
  const city = currentRegion(useRegionsNow()).name
  const counted = (except: Facet, get: (e: FeedRow) => string | null) => {
    const scoped = applyFacets(rows, except)
    const counts = new Map<string, number>()
    for (const e of scoped) {
      const v = get(e)
      if (v) counts.set(v, (counts.get(v) ?? 0) + 1)
    }
    return counts
  }

  const langCounts = counted('lang', e => e.english_access)
  const districtCounts = counted('district', e => e.district)
  const formCounts = counted('form', e => classicalForm(e))
  const orgCounts = counted('org', e => e.organisation)

  const chipsFor = (
    key: Facet, counts: Map<string, number>, label: (v: string) => string,
    order?: (a: string, b: string) => number,
  ) => {
    const values = [...counts.keys()].sort(order ?? ((a, b) => a.localeCompare(b)))
    return values.map(v => (
      <Chip key={v} on={f[key].includes(v)} count={counts.get(v)}
            onClick={() => setF({ ...f, [key]: toggle(f[key], v) })}>
        {label(v)}
      </Chip>
    ))
  }

  const formOrder = Object.keys(FORM_LABEL) as ClassicalForm[]

  return (
    <Sheet
      title="Filters"
      open
      onOpenChange={o => { if (!o) onClose() }}
      actions={(
        <>
          <Button variant="quiet" onClick={onReset}>Reset</Button>
          <Button onClick={onClose}>Show {count} events</Button>
        </>
      )}
    >
      <p className="u-field__label">When</p>
      {/* a4-14: five windows, the desk's words. Five do not fit one track
          at 358px ("Next 30 days" alone is 100px), so they are chips, which
          wrap — the sheet's own facet shape below. */}
      <div className="browse__chips" role="group" aria-label="When">
        {WINDOWS.map(w => (
          <Chip key={w.when} on={when === w.when} onClick={() => setWhen(w.when)}>
            {w.label}
          </Chip>
        ))}
      </div>

      <p className="u-field__label">Where</p>
      <Segmented
        value={f.where}
        onChange={v => setF({ ...f, where: v as Filters['where'] })}
        /* Four segments in 358px (a 390 phone less the sheet's gutters):
           A3's Day trips made the old "Berlin only" and "Everywhere" run
           the track 37px off the right edge, so the two short ones got
           shorter. "Berlin" beside "Berlin and reach" still reads as only. */
        options={[
          { value: 'reach', label: `${city} and reach` },
          { value: 'here', label: city },
          { value: 'trips', label: 'Day trips' },
          { value: 'all', label: 'All' },
        ]}
      />

      <p className="u-field__label">Sort</p>
      <Segmented
        value={sort}
        onChange={v => setSort(v as SortKey)}
        options={BROWSE_SORTS.map(k => ({ value: k, label: SORTS[k].label }))}
      />

      <p className="u-field__label">Language</p>
      <div className="browse__chips">
        {chipsFor('lang', langCounts, v => EN_LABEL[v]?.[0] ?? v)}
      </div>

      <p className="u-field__label">District</p>
      <div className="browse__chips">
        {chipsFor('district', districtCounts, v => v)}
      </div>

      <p className="u-field__label">Classical form</p>
      <div className="browse__chips">
        {chipsFor('form', formCounts, v => FORM_LABEL[v as ClassicalForm] ?? v,
          (a, b) => formOrder.indexOf(a as ClassicalForm) - formOrder.indexOf(b as ClassicalForm))}
      </div>

      <p className="u-field__label">Source</p>
      <div className="browse__chips">
        {chipsFor('org', orgCounts, v => v)}
      </div>
    </Sheet>
  )
}
